import React, { useCallback, useEffect, useState } from 'react';
import { Timer, Loader2, RefreshCw, TrendingUp, TrendingDown } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, ReferenceLine, YAxis, XAxis, Tooltip } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import api from '../../lib/api';

const sec = (ms) => `${(ms / 1000).toFixed(ms < 10000 ? 1 : 0)}s`;
const tone = (ms, slow) => (ms >= slow ? 'text-rose-700' : ms >= slow / 2 ? 'text-amber-700' : 'text-emerald-700');
const stroke = (ms, slow) => (ms >= slow ? '#e11d48' : ms >= slow / 2 ? '#d97706' : '#0f766e');

// Data Health → each page's worst daily warm-up time (Prepare demo runs) so a creeping slowdown
// is visible long before it crosses the 25s alert line.
export default function WarmupTrendCard() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    setBusy(true);
    api.get(`/health-cache/warmup-trend?days=${days}`).then((r) => setD(r.data)).catch(() => setD({ pages: [], runs: 0 })).finally(() => setBusy(false));
  }, [days]);
  useEffect(() => { load(); }, [load]);
  const slow = d?.slow_ms || 25000;
  const ceiling = Math.max(slow * 1.2, ...(d?.pages || []).map((p) => p.worst_ms || 0));

  return (
    <Card data-testid="warmup-trend-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><Timer className="h-4 w-4 text-teal-600" /> Warm-up trend · page settle time per day</CardTitle>
        <CardDescription>Worst time each page took to settle during Prepare demo runs. The dashed line is the {slow / 1000}s alert threshold — watch for lines drifting towards it.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {[7, 30, 90].map((n) => <button key={n} type="button" onClick={() => setDays(n)} className={cn('rounded-md px-2 py-1 text-xs font-semibold', days === n ? 'bg-teal-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')} data-testid={`warmup-trend-window-${n}`}>{n} days</button>)}
          <Button size="sm" variant="ghost" onClick={load} data-testid="warmup-trend-refresh">{busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />} Refresh</Button>
          {d && <span className="text-xs text-slate-600" data-testid="warmup-trend-summary"><b>{d.runs}</b> warm-up run{d.runs === 1 ? '' : 's'}{d.orgs?.length ? ` · ${d.orgs.length} org${d.orgs.length > 1 ? 's' : ''}` : ''}</span>}
        </div>
        {d && d.pages.length === 0 && <p className="text-xs text-slate-500" data-testid="warmup-trend-empty">No warm-up runs in this window — use Prepare demo from the Tools pill to start the trend.</p>}
        {d?.pages.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="warmup-trend-grid">
            {d.pages.map((p) => {
              const up = p.trend_pct >= 25 && p.latest_ms > 3000;
              const down = p.trend_pct <= -25;
              return (
                <div key={p.path} className="rounded-lg border border-slate-200 bg-white p-2.5" data-testid={`warmup-trend-${p.path === '/' ? 'home' : p.path.replace(/\//g, '-').slice(1)}`} data-slow={p.latest_ms >= slow ? 'true' : 'false'}>
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <span className="truncate font-semibold text-slate-800">{p.label}</span>
                    <span className={cn('flex items-center gap-1 font-mono tabular-nums', tone(p.latest_ms, slow))}>
                      {up && <TrendingUp className="h-3 w-3" aria-label="getting slower" />}{down && <TrendingDown className="h-3 w-3" aria-label="getting faster" />}{sec(p.latest_ms)}
                    </span>
                  </div>
                  <div className="h-16 mt-1">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={p.points} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                        <XAxis dataKey="day" hide />
                        <YAxis domain={[0, ceiling]} hide />
                        <ReferenceLine y={slow} stroke="#e11d48" strokeDasharray="3 3" strokeOpacity={0.6} />
                        <Tooltip formatter={(v, _n, item) => [`${sec(v)}${item?.payload?.timeouts ? ` · ${item.payload.timeouts} timeout` : ''}`, 'worst']} labelFormatter={(l) => l} contentStyle={{ fontSize: 11, padding: '4px 8px' }} />
                        <Line type="monotone" dataKey="ms" stroke={stroke(p.worst_ms, slow)} strokeWidth={2} dot={p.points.length < 12} isAnimationActive={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-1 flex justify-between text-[10px] text-slate-500">
                    <span>worst {sec(p.worst_ms)}</span>
                    <span className={cn(up && 'text-amber-700 font-semibold', down && 'text-emerald-700')}>{p.trend_pct > 0 ? '+' : ''}{p.trend_pct}% vs earlier</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
